import { useSeoMeta } from '@unhead/react';
import { BadgeCheck, Highlighter } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { HighlightCardSkeleton, HighlightList } from '@/components/HighlightList';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { useAuthor } from '@/hooks/useAuthor';
import { useAuthorHighlightsFeed, useParsedPages } from '@/hooks/useHighlights';
import { getDisplayName } from '@/lib/getDisplayName';
import { sanitizeUrl } from '@/lib/sanitizeUrl';

interface ProfilePageProps {
  pubkey: string;
}

export function ProfilePage({ pubkey }: ProfilePageProps) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;

  const feed = useAuthorHighlightsFeed(pubkey);
  const highlights = useParsedPages(feed.data?.pages);

  const name = getDisplayName(metadata, pubkey);
  const picture = sanitizeUrl(metadata?.picture);
  const banner = sanitizeUrl(metadata?.banner);
  const nip05 = metadata?.nip05;

  useSeoMeta({
    title: `${name} — glowmark`,
    description: metadata?.about || `Highlights by ${name} on glowmark.`,
  });

  return (
    <AppShell>
      <div className="container max-w-2xl py-8 space-y-8">
        {/* Profile header */}
        <div className="space-y-4">
          <div className="h-32 sm:h-40 rounded-xl overflow-hidden border border-border bg-secondary">
            {banner ? (
              <img src={banner} alt="" className="size-full object-cover" loading="lazy" />
            ) : (
              <div className="size-full bg-[linear-gradient(120deg,hsl(45_95%_65%/0.5),hsl(38_60%_80%/0.4))] dark:bg-[linear-gradient(120deg,hsl(45_80%_40%/0.3),hsl(35_30%_25%/0.4))]" />
            )}
          </div>

          <div className="flex items-start gap-4 -mt-10 px-4">
            <Avatar className="size-20 border-4 border-background shadow-md shrink-0">
              {picture && <AvatarImage src={picture} alt={name} />}
              <AvatarFallback className="bg-secondary text-secondary-foreground text-xl font-semibold">
                {name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>

          <div className="px-4 space-y-2">
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-1.5">
              {name}
              {nip05 && <BadgeCheck className="size-5 text-primary" aria-label={`Verified: ${nip05}`} />}
            </h1>
            {nip05 && <p className="text-sm text-muted-foreground">{nip05}</p>}
            {metadata?.about && (
              <p className="text-sm leading-relaxed text-foreground/80 max-w-lg">{metadata.about}</p>
            )}
          </div>
        </div>

        {/* Their highlights */}
        <section className="space-y-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold px-1">
            <Highlighter className="size-4.5 text-primary" />
            Highlights
          </h2>

          {feed.isPending ? (
            <div className="space-y-4">
              <HighlightCardSkeleton />
              <HighlightCardSkeleton />
            </div>
          ) : feed.isError ? (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  Couldn't load this profile's highlights right now.
                </p>
              </CardContent>
            </Card>
          ) : highlights.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 px-8 text-center">
                <p className="text-muted-foreground max-w-sm mx-auto">
                  No highlights from {name} yet.
                </p>
              </CardContent>
            </Card>
          ) : (
            <HighlightList
              highlights={highlights}
              hasNextPage={feed.hasNextPage}
              isFetchingNextPage={feed.isFetchingNextPage}
              fetchNextPage={feed.fetchNextPage}
            />
          )}
        </section>
      </div>
    </AppShell>
  );
}
